<template>
  <div
    id="product-form"
    ref="productForm"
    class="product-form"
    :class="{ [$class('loading')]: loading }"
    data-sign-post-location="main"
    js-sign-post
  >
    <form class="product-form__form" @submit.prevent="handleAddToCart">
      <product-prices
        class="product-form__prices-container"
        :compare-at-price="variant.compareAtPrice?.amount"
        :discount-logic="discountLogic"
        :loading="loading"
        :price="variant.price?.amount"
        :product="product"
        template="form"
        :unit-price="variant?.unitPrice?.amount"
        :unit-price-measurement="variant?.unitPriceMeasurement"
        :varies="variant.price?.varies"
        :is-gift-card="product.giftCard"
        :variant-id="variant.id"
      />

      <product-promoted-campaign
        v-if="getComponentVisible('product-promoted-campaign')"
        v-cloak
        class="product-form__promoted-campaign"
        :discount-logic="discountLogic"
        :product="product"
        :variant="variant"
      />

      <input name="quantity" readonly type="hidden" :value="quantity" />

      <product-options
        class="product-form__options"
        :loading="loading"
        :product="product"
        :section="section"
        :variant="variant"
        :bundle="bundle"
        @update-variant="handleUpdateVariant"
        @bundle-extra-item="bundleExtraItems"
        @update-bundle-status="handleBundleStatus"
      />

      <recipient-form
        v-if="false"
        class="product-form__recipient-form"
        :gift-card="giftCard"
        :section="section"
        @update-recipient-form="handleUpdateRecipientForm"
      />

      <low-inventory-alert :variant="variant" />

      <div
        v-if="
          !getComponentActive('product-promoted-campaign') &&
          loadProductPromotedCampaign
        "
        class="product-form__promoted-campaign"
        :class="[$class('loading')]"
      />

      <notify-me
        v-if="
          (!isVariantAvailable ||
            isVariantBackSoonOrSoldOut ||
            bundleIsSoldout) &&
          product.type !== 'bundle' &&
          !ctaHidden
        "
        ref="notifyMe"
        :variant-id="variant.id"
        :product-id="product.id"
        @update-success="handleNotifyMeSuccess"
      />

      <div
        v-if="variant && variant.maxQuantity && variant.maxQuantity > 0"
        class="product-form__product-notice"
      >
        <notification-panel
          class-container="product-form__max-quantity-notice"
          :show-close="false"
          :text="
            $string('product.form.max_quantity_notice', {
              replace: { maxQuantity: variant.maxQuantity },
            })
          "
          type="info"
        />
      </div>
      <div
        v-if="!ctaHidden && !showSuccess"
        ref="addToCart"
        class="product-form__add-to-cart-container"
        :class="{
          [$class('fixed')]: isSticky && isStickyButtonEnabled,
        }"
        :style="styleTransform"
      >
        <div
          v-if="isSticky && isStickyButtonEnabled"
          class="product-form__sticky-info"
        >
          <span
            v-if="variantTitle"
            class="product-form__sticky-variant-subtitle title-mobile-m-bold text-desktop-paragraph-m-bold"
            v-text="variantTitle"
          />

          <product-prices
            :compare-at-price="variant.compareAtPrice?.amount"
            :discount-logic="discountLogic"
            :loading="loading"
            :price="variant.price?.amount"
            template="sticky"
            :unit-price="variant?.unitPrice?.amount"
            :unit-price-measurement="variant?.unitPriceMeasurement"
            :varies="variant.price?.varies"
            :is-gift-card="product.giftCard"
            show-savings-notification
          />
        </div>

        <btn
          class="product-form__add-to-cart"
          :label="addToCartText"
          :modifiers="getButtonModifiers()"
          size="semimedium"
          :show-state-icon="state === 'adding'"
          :show-icon="showNotificationPanel"
          :disabled="bundleIsSoldout"
          type="submit"
        >
          <template #stateIcon>
            <icon-loading />
          </template>

          <template #icon>
            <icon-email v-if="showNotificationPanel" />
          </template>
        </btn>
      </div>

      <div
        v-if="isSticky"
        class="product-form__sticky-spacer"
        :style="{ height: `${spacerHeight}px` }"
      />
    </form>

    <notification-panel
      v-if="error"
      class-container="product-form__error"
      :show-close="false"
      :text="error"
      type="error"
    />

    <delivery-time
      v-if="showDeliveryTime || (loading && hasLiquidDeliveryTime)"
      class="product-form__delivery"
      :class="{
        'product-form__delivery--centered':
          !product.socialSelling &&
          !variant.socialSelling &&
          (!product.socialSellingList || !product.socialSellingList.length),
      }"
      :is-gift-card="product.giftCard"
      :loading="loading"
    />

    <template v-if="loading && liquidSocialSellingCount > 0">
      <social-selling
        v-for="element of liquidSocialSellingCount"
        :key="`loading-${element}`"
        :product="product"
        :selling-element="{}"
        :loading
      />
    </template>

    <template v-else>
      <social-selling
        v-if="product.socialSelling"
        :product="product"
        :variant="variant"
        :selling-element="product.socialSelling"
      />

      <social-selling
        v-if="variant.socialSelling"
        :product="product"
        :variant="variant"
        :selling-element="variant.socialSelling"
      />

      <div
        v-if="product.socialSellingList && product.socialSellingList.length"
        class="product-form__social-selling-list"
      >
        <template
          v-for="element of product.socialSellingList"
          :key="element.id"
        >
          <social-selling
            :product="product"
            :variant="variant"
            :selling-element="element"
            is-list
          />
        </template>
      </div>
    </template>

    <oos-alternatives
      v-if="
        isESN64TestVariation1 &&
        (!isVariantAvailable || isVariantBackSoonOrSoldOut) &&
        product.type !== 'bundle'
      "
      :variant="variant"
    />
  </div>
</template>

<script>
/**
 * Vue: Product form (product-form)
 * -----------------------------------------------------------------------------
 * Product form to handle variant selection and adding to cart.
 *
 * @param {Boolean} [loading] - Loading state.
 * @param {Object|Boolean} product - Canvas formatted product object.
 * @param {Object} section - Main product section settings.
 * @param {Object|Boolean} variant - Canvas formatted variant object.
 * @param {Object} bundle - bundle meta object.
 *
 * @emits updateVariant - Emit new variant object.
 *
 */
// eslint-disable-next-line import/named
import { defineAsyncComponent } from 'vue';
import { mapActions, mapState } from 'pinia';
import useOverlaysStore from '~stores/overlays';
import useIndexStore from '~stores/index';
import useComponentsStore from '~stores/components';
import useCartStore from '~stores/cart';
import useDiscountCampaignsStore from '~stores/discount-campaigns';
import useToastNotificationsStore from '~stores/toast-notifications';

import { formatTrackingItem } from '~/format/format-tracking-item';
import {
  getMaxQuantityErrorMessage,
  setIntersectionObserver,
} from '~/helpers/general';
import { isInVariation, waitForKameleoon } from '~/helpers/kameleoon';
import { formatCartError } from '~/helpers/format-cart-error';

import Btn from '~global/btn/btn';
import DeliveryTime from '~async/delivery-time/delivery-time';
import NotificationPanel from '~async/utils/notification-panel/notification-panel';
import NotifyMe from '~async/product-form/notify-me';
import ProductOptions from '~async/product-form/product-options';
import ProductPrices from '~global/product-prices/product-prices';
import LowInventoryAlert from '~async/low-inventory-alert/low-inventory-alert';
import OosAlternatives from '~async/product-form/oos-alternatives';
import RecipientForm from '~async/product-form/recipient-form';
import SocialSelling from '~async/social-selling/social-selling';

import IconLoading from '~icons/general/loading.svg';
import IconEmail from '~icons/general/email.svg';

import { computeDiscountLogic, emitUpdateVariant } from '~/helpers/vue';

export default {
  name: 'ProductForm',

  components: {
    Btn,
    DeliveryTime,
    IconLoading,
    IconEmail,
    LowInventoryAlert,
    NotificationPanel,
    NotifyMe,
    OosAlternatives,
    ProductOptions,
    ProductPrices,
    ProductPromotedCampaign: defineAsyncComponent({
      loader: () =>
        import(
          /* webpackChunkName: 'component.product-promoted-campaign' */
          '~async/product-promoted-campaign/product-promoted-campaign'
        ),
    }),
    RecipientForm,
    SocialSelling,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    bundle: {
      type: Object,
      default: () => ({}),
    },
    product: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
    section: {
      type: Object,
      default: () => ({
        id: '',
      }),
      required: true,
    },
    variant: {
      type: [Boolean, Object],
      default: false,
      required: true,
    },
  },

  emits: {
    'update-variant': emitUpdateVariant,
    'update-bundle-status': (payload) => {
      return typeof payload === 'boolean';
    },
  },

  data() {
    return {
      error: '',
      giftCard: {
        enableForm: false,
        fields: {
          Message: '',
          'Recipient email': '',
          'Recipient name': '',
          'Send on': '',
        },
        timezoneOffset: '',
      },
      lineItemProperties: {},
      location: 'PDP',
      quantity: 1,
      showCta: true,
      showSuccess: false,
      spacerHeight: 0,
      state: 'ready',
      timeout: {},
      bundleExtraItem: {},
      bundleIsSoldout: false,
      liquidSocialSellingCount: 0,
      hasLiquidDeliveryTime: false,
      isGraduatedDiscount: false,
      stickyMobileDisabledByTest: false,
      intersectionObserver: null,
      skipStickyTransition: false,
      esn64: {
        experimentId: 402323,
        activeVariation: 0,
        variations: { 1: 1338391 },
      },
      esn64PollCancel: null,
    };
  },

  computed: {
    ...mapState(useIndexStore, {
      isMobile: (store) => store.screen.mobile,
      isSticky: (store) => store.stickyCta.sticky,
      isStickyVisible: 'getStickyCtaVisible',
    }),

    ...mapState(useDiscountCampaignsStore, {
      getCampaignDiscount: 'getCampaignDiscount',
      promotedCampaign: 'promoted',
      isVariantInGraduatedDiscount: 'isVariantInGraduatedDiscount',
    }),
    ...mapState(useCartStore, {
      depositItemUpdate: 'depositItemUpdate',
    }),

    /**
     * Compute add to cart text.
     * @returns {String}
     */
    addToCartText() {
      if (!this.variant && !this.loading) {
        return this.$string('product.cta.select_variant');
      }

      if (this.variant && (!this.variant.available || this.bundleIsSoldout)) {
        if (this.product.type !== 'bundle') {
          return this.$string('product.cta.notify_me');
        }
        return this.$string('product.cta.sold_out');
      }

      return this.$string('product.cta.add_to_cart');
    },

    /**
     * Compute whether to show notification panel.
     * @returns {Boolean}
     */
    showNotificationPanel() {
      return !this.variant.available && this.product.type !== 'bundle';
    },

    /**
     * Compute discount logic.
     * @returns {Object}
     */
    discountLogic() {
      return computeDiscountLogic({
        discountPreview: !this.product.giftCard,
        onSale: this.variant?.compareAtPriceDiscount,
        product: this.product,
        variant: this.variant,
      });
    },

    /**
     * Compute gift card line item properties.
     * @returns {Null|Object}
     */
    giftCardLineItemProperties() {
      if (
        !this.product.giftCard ||
        this.giftCard.fields['Recipient email'] === ''
      ) {
        return null;
      }

      const filteredFields = {};

      Object.entries(this.giftCard.fields).forEach(([key, value]) => {
        if (!value) {
          return;
        }

        const modifiedKey = `__${key.replace(/ /g, '_').toLowerCase()}`;

        filteredFields[modifiedKey] = value;
      });
      return {
        __shopify_send_gift_card_to_recipient: 'on',
        __shopify_offset: this.giftCard.timezoneOffset,
        ...filteredFields,
      };
    },

    /**
     * Compute whether to load product promoted campaign component.
     * checks also if the active campaign has graduated discounts
     * and show the component according to that
     * @returns {Boolean}
     */
    loadProductPromotedCampaign() {
      const campaign = this.promotedCampaign || this.isGraduatedDiscount;
      return !this.loading && campaign;
    },

    /**
     * Checks if the current variant is available.
     * @returns {boolean} True if the variant exists and is available.
     */
    isVariantAvailable() {
      return this.variant?.available;
    },

    /**
     * Checks if the current variant's state is either 'back-soon'
     * or 'sold-out'.
     * @returns {boolean} True if the variant's availableState is 'back-soon'
     * or 'sold-out'.
     */
    isVariantBackSoonOrSoldOut() {
      return ['back-soon', 'sold-out'].includes(this.variant.availableState);
    },

    /**
     * Determines if the delivery time should be shown.
     * Combines the conditions from `showCta`, `isVariantAvailable`,
     * and `isVariantBackSoonOrSoldOut`. Shows the delivery time only, if
     * the product is available and the CTA is shown.
     * @returns {boolean} True if the delivery time should be shown.
     */
    showDeliveryTime() {
      return (
        this.showCta &&
        this.isVariantAvailable &&
        !this.isVariantBackSoonOrSoldOut
      );
    },

    /**
     * Compute sticky CTA transform.
     * - Slides down by its own height plus the fixed container's `bottom`
     *   offset, since on desktop `.is-fixed` sits `var(--spacing-l)` above
     *   the viewport edge (mobile is flush at `bottom: 0`) - translating by
     *   100% alone would leave that gap's worth of the bar still visible.
     * - When it starts out blocked, `skipStickyTransition` suppresses the
     *   transition for the one frame `.is-fixed` (and its transform
     *   transition) gets added: otherwise the transform value flips from
     *   the resting `translateY(0)` to the hidden value in the same patch
     *   that switches to `position: fixed`, and the bar visibly slides from
     *   its normal spot into the hidden one instead of just appearing
     *   already hidden.
     */
    styleTransform() {
      const style = this.skipStickyTransition ? { transition: 'none' } : {};

      if (this.isSticky && this.isStickyVisible)
        return { ...style, transform: 'translateY(0)' };
      if (this.isSticky)
        return {
          ...style,
          transform: 'translateY(calc(100% + var(--spacing-l)))',
        };
      return { ...style, transform: 'translateY(0)' };
    },

    ctaHidden() {
      return this.section.hideCtaButton;
    },

    /**
     * Determine if sticky button should be enabled based on AB test.
     * - Option A: disabled for mobile
     * - otherwise just use theme setting
     * @returns {Boolean}
     */
    isStickyButtonEnabled() {
      // Option A: Sticky disabled on mobile
      if (this.stickyMobileDisabledByTest) {
        return false;
      }

      return this.section.isStickyButton;
    },

    /**
     * Get variant title (size/flavor) or product title for sticky display.
     * Replaces "/" with ", " for better readability.
     * @returns {String}
     */
    variantTitle() {
      if (!this.variant?.title || this.variant.title === 'Default Title') {
        return this.product?.title ?? '';
      }
      return this.variant.title.replace(/\s*\/\s*/g, ', ');
    },

    /**
     * Kameleoon AB test ESN-64: OOS alternatives.
     * @returns {Boolean}
     */
    isESN64TestVariation1() {
      return this.esn64.activeVariation === this.esn64.variations[1];
    },
  },

  watch: {
    /**
     * Watch isMobile to clear isSticky.
     */
    isMobile() {
      if (this.isMobile) {
        return;
      }

      this.setStickyCta({
        sticky: false,
        visible: false,
      });
    },

    /**
     * Watch load state to load component.
     */
    loadProductPromotedCampaign() {
      if (!this.loadProductPromotedCampaign) {
        return;
      }

      this.loadComponent('product-promoted-campaign');
    },

    /**
     * Watch variant to reset quantity and error and show CTA states.
     */
    async variant() {
      this.error = '';
      this.showCta = true;
      this.showSuccess = false;
      this.isGraduatedDiscount = await this.isCurrentItemGraduated();

      /**
       * Kameleoon AB test ESN-64: re-check the experiment on every OOS
       * selection, not just on mount.
       */
      if (!this.isVariantAvailable || this.isVariantBackSoonOrSoldOut) {
        this.initESN64Experiment();
      }
    },

    /**
     * Suppress the sticky transform transition for the frame `isSticky`
     * first becomes true, so `.is-fixed` (and its transition) being added
     * doesn't animate the bar visibly through its resting position when it
     * starts out blocked.
     */
    isSticky(value) {
      if (!value) {
        return;
      }

      this.skipStickyTransition = true;
      this.$nextTick().then(() => {
        requestAnimationFrame(() => {
          this.skipStickyTransition = false;
        });
      });
    },
  },

  beforeMount() {
    this.giftCard.timezoneOffset = new Date().getTimezoneOffset().toString();

    // Count liquid social-selling elements and check for delivery-time and promoted-campaign for loading state
    if (this.loading) {
      const liquidElements = document.querySelectorAll(
        '#product-form .social-selling.liquid',
      );
      this.liquidSocialSellingCount = liquidElements.length;

      const liquidDeliveryTime = document.querySelector(
        '#product-form .delivery-time.liquid',
      );
      this.hasLiquidDeliveryTime = Boolean(liquidDeliveryTime);
    }
  },

  async mounted() {
    this.addIntersectionObserver();
    this.isGraduatedDiscount = await this.isCurrentItemGraduated();
    this.initESN56Experiment();
    this.initESN64Experiment();
  },

  unmounted() {
    this.esn64PollCancel?.();
    this.intersectionObserver?.disconnect();
  },

  methods: {
    ...mapActions(useComponentsStore, {
      loadComponent: 'load',
    }),
    ...mapActions(useCartStore, {
      addItem: 'addItem',
      setDepositItemUpdate: 'setDepositItemUpdate',
      getCartAttributeCode: 'getCartAttributeCode',
    }),
    ...mapActions(useOverlaysStore, {
      openOverlay: 'open',
    }),
    ...mapActions(useIndexStore, {
      setStickyCta: 'setStickyCta',
    }),
    ...mapActions(useToastNotificationsStore, ['addToast']),

    /**
     * Push Custom Product Properties to Line Item Properties from Bundle
     * Item from the bundle where customers cannot select anything
     */
    bundleExtraItems(offsetBundle) {
      // const properties = {}
      if (offsetBundle) {
        const mappedBundles = offsetBundle.map((item) => {
          return item.currentOptionName;
        });

        this.lineItemProperties.extraBundle = mappedBundles;
      }
    },

    /**
     * Kameleoon integration kept short by purpose
     * We just have to do something in one case
     */
    initESN56Experiment() {
      waitForKameleoon({
        experimentId: 381781,
        onReady: (experiment) => {
          if (isInVariation(381781, 0, experiment)) {
            this.stickyMobileDisabledByTest = true;
          }
        },
      });
    },

    /**
     * Kameleoon AB test ESN-64: OOS alternatives.
     * - Cancels any still-pending previous poll before starting a new one,
     *   since this can now be called on every OOS variant selection, not
     *   just on mount.
     * - Skips starting a new poll once the experiment has already resolved
     *   to a variation.
     */
    initESN64Experiment() {
      if (this.esn64.activeVariation !== 0) {
        return;
      }

      this.esn64PollCancel?.();

      this.esn64PollCancel = waitForKameleoon({
        experimentId: this.esn64.experimentId,
        onReady: (experiment) => {
          for (const variation of Object.values(this.esn64.variations)) {
            if (isInVariation(this.esn64.experimentId, variation, experiment)) {
              this.esn64.activeVariation = variation;
              break;
            }
          }
        },
      });
    },

    /**
     * Set intersection observer for sticky CTA.
     */
    addIntersectionObserver() {
      if (!this.$refs.productForm) {
        return;
      }

      this.intersectionObserver = setIntersectionObserver({
        callback: this.handleIntersection,
        element: this.$refs.productForm,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
    },

    /**
     * Handle intersection.
     * @param {Array} entries - Intersection entries.
     */
    handleIntersection(entries) {
      // AB Test: Check if sticky should be disabled
      if (!this.isStickyButtonEnabled) {
        this.setStickyCta({
          sticky: false,
          visible: false,
        });
        return;
      }

      // Original logic only worked for mobile, now also support desktop
      entries.forEach((entry) => {
        if (this.showSuccess) {
          return;
        }

        const isSticky =
          !entry.isIntersecting && entry.boundingClientRect.bottom < 0;

        if (!isSticky) {
          this.setStickyCta({
            sticky: false,
            visible: false,
          });
          return;
        }

        this.spacerHeight = this.$refs.addToCart
          ? this.$refs.addToCart.offsetHeight
          : 0;

        // Emit an event when isSticky changes
        this.setStickyCta({
          sticky: true,
          visible: true,
        });
      });
    },

    /**
     * Handle add to cart submission.
     */
    async handleAddToCart() {
      let depositVariantId;

      if (!this.variant) {
        this.error = this.$string('product.form.select_variant');
        return;
      }

      clearTimeout(this.timeout);
      this.error = '';
      this.state = 'adding';

      /**
       * Notify me.
       * - Uses child component method.
       */
      if (!this.variant.available) {
        try {
          await this.$refs.notifyMe.handleNotifyMe();
          this.state = 'ready';
        } catch (error) {
          this.state = 'error';
          cnvs.ReportError('Failed to submit notify me', error);
        }

        return;
      }

      const items = [
        {
          id: this.variant.id,
          properties: this.product.giftCard
            ? {
                ...this.giftCardLineItemProperties,
                ...this.lineItemProperties,
              }
            : this.lineItemProperties,
          bundle: this.product.type === 'bundle',
          hasLocalizedImage: Boolean(this.variant.localizedImage),
          quantity: this.quantity,
          productId: this.product.id,
          productMaxQuantity: this.product.maxQuantity,
          variantMaxQuantity: this.variant.maxQuantity,
        },
      ];

      if (
        this.variant.canDeposit === 'true' &&
        this.$variable('store.country') === 'DE'
      ) {
        depositVariantId = this.variant.id;
        this.setDepositItemUpdate([
          ...this.depositItemUpdate,
          depositVariantId,
        ]);

        const depositProduct = {
          id: this.$variable('product.depositProductID'),
          quantity: this.variant.canDepositAmount * this.quantity,
        };
        items.push(depositProduct);
      }

      /**
       * Add to cart.
       */
      try {
        await this.addItem(items);
        this.state = 'added';

        // Tracking event add qty to item in cart
        this.handleTrackingAddItem();

        this.openOverlay({
          component: 'cart-drawer',
          ignoreDismissed: true,
          namespace: 'cartDrawer',
        });

        await this.resetState();
      } catch (error) {
        this.state = 'error';

        // Handle maxQuantity errors
        if (error.maxQuantityError) {
          const message = getMaxQuantityErrorMessage(
            error.maxQuantityType,
            error.maxQuantityValue,
          );

          this.addToast({
            title: message,
            id: 'max-quantity-limit-error',
            type: 'error',
          });
          this.state = 'ready';
          return;
        }

        if (error.description) {
          this.handleErrorMessage(error.description);
          return;
        }

        if (error.message === 'Cart limit reached') {
          this.handleErrorMessage(error.message);
          return;
        }

        cnvs.ReportError('Failed to add product to cart', error);
      }

      this.setDepositItemUpdate(
        this.depositItemUpdate.filter((id) => id !== depositVariantId),
      );
    },

    /**
     * Reset product form state after adding to cart after message delay.
     */
    async resetState() {
      await new Promise((resolve) => {
        setTimeout(resolve, this.$timing('message'));
      });

      this.state = 'ready';

      /**
       * Reset gift card recipient form based on setting.
       */
      if (this.product.giftCard) {
        this.giftCard = {
          enableForm: false,
          fields: {
            Message: '',
            'Recipient email': '',
            'Recipient name': '',
            'Send on': '',
          },
        };
      }
    },

    /**
     * Handle update variant event.
     * @param {Object} variant - Emitted variant object.
     */
    handleUpdateVariant(variant) {
      this.$emit('update-variant', variant);
    },

    /**
     * Handles the add to cart button depending on bundle status
     * @param status Boolean
     */
    handleBundleStatus(status) {
      this.bundleIsSoldout = status;
      this.$emit('update-bundle-status', status);
    },

    /**
     * Handle update quantity event.
     * @param {Number} quantity - Emitted quantity.
     */
    handleQuantityUpdate(quantity) {
      this.quantity = quantity;
    },

    /**
     * Handle update on the notify me component - if requests has success.
     * @param {Boolean} success - Whether the request was successful or not.
     */
    handleNotifyMeSuccess(success) {
      if (success) {
        this.showCta = false;
        this.showSuccess = true;
      } else {
        this.state = 'error';
      }
    },

    /**
     * Handle update recipient form event.
     * @param {Object} recipientForm - Emitted recipient form.
     */
    handleUpdateRecipientForm(recipientForm) {
      this.giftCard = recipientForm;
    },

    /**
     * Handle error message.
     * @param {Object} errorDescription - Error description from add request.
     */
    handleErrorMessage(errorDescription) {
      this.error = formatCartError(errorDescription, '<br>');
    },

    /**
     * Get button modifiers based on variant availability and bundle status.
     * @returns {String}
     */
    getButtonModifiers() {
      // Show secondary when variant is not available and showing notify me form
      if (!this.variant.available) {
        return 'center secondary';
      }

      // Show custom when loading, variant available, or bundle not sold out
      if (this.loading || this.variant.available || !this.bundleIsSoldout) {
        return 'center custom';
      }

      // Default to secondary (when variant unavailable and bundle sold out)
      return 'center secondary';
    },

    /**
     * Tracking event add qty to item in cart.
     */
    handleTrackingAddItem() {
      const campaignDiscount = this.getCampaignDiscount(
        this.discountLogic,
        'active',
      );
      const discountObject = {
        discountAmount: campaignDiscount?.amount,
        calculationType: campaignDiscount?.calculationType,
        discountCode: this.getCartAttributeCode(),
      };
      const deliveryTime = this.$string('product.delivery_time', {
        replace: { time: this.$variable('product.deliveryTime') },
      });
      const currencyCode = this.$variable('store.currency');

      const item = formatTrackingItem({
        product: this.product,
        variant: this.variant,
        template: 'PDP',
        discountObject,
        deliveryTime,
        currencyCode,
        quantity: this.quantity,
      });

      cnvs.EventBus.emit('dataLayer:add_to_cart', {
        event: 'add_to_cart',
        currency: cnvs.store.currency,
        location: 'PDP',
        items: [item],
      });
    },

    /**
     * Check if variant is part of the graduated discount
     * we have to wait here as via search the variantId isn't available right away
     * @returns {Boolean}
     */
    async isCurrentItemGraduated() {
      await this.$nextTick();
      const variantId = this.getVariantFromUrl();
      return this.isVariantInGraduatedDiscount(variantId, 'active');
    },

    /**
     * Get initial variant from URL parameter.
     * @returns {String}
     */
    getVariantFromUrl() {
      const params = new URLSearchParams(document.location.search.substring(1));
      const variantId = Number(params.get('variant'));
      return variantId;
    },
  },
};
</script>

<style lang="scss">
@import './product-form';
</style>
